/**
 * Утиліти для роботи з Google Tag Manager (GTM)
 * Підтримка багатомовності і відстеження подій за мовами
 */

// ID для Google Tag Manager
export const GTM_ID = process.env.REACT_APP_GTM_ID || 'GTM-XXXXXXX';

/**
 * Ініціалізація Google Tag Manager і створення dataLayer
 */
export const initDataLayer = () => {
  // Створюємо dataLayer, якщо його ще немає
  window.dataLayer = window.dataLayer || [];
  
  // Функція gtag для Consent Mode
  function gtag(){window.dataLayer.push(arguments);}
  window.gtag = gtag;
  
  // Встановлюємо значення Consent Mode за замовчуванням
  gtag('consent', 'default', {
    'ad_storage': 'denied',
    'ad_user_data': 'denied',
    'ad_personalization': 'denied',
    'analytics_storage': 'denied'
  });
  
  // Увімкнення URL passthrough для покращення вимірювання ефективності реклами
  gtag('set', 'url_passthrough', true);
  
  // Ініціалізуємо GTM
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js'
  });
  
  return window.dataLayer;
};

/**
 * Оновлює налаштування згоди користувача на основі вибору
 * @param {string} consentType - Тип згоди: 'all', 'analytics', 'ads', 'none'
 */
export const updateConsent = (consentType) => {
  if (typeof window === 'undefined' || !window.gtag) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('GTM: gtag не ініціалізовано');
    }
    return;
  }
  
  try {
    switch (consentType) {
      case 'all':
        // Користувач надав згоду на всі типи обробки даних
        window.gtag('consent', 'update', {
          'ad_storage': 'granted',
          'ad_user_data': 'granted',
          'ad_personalization': 'granted',
          'analytics_storage': 'granted'
        });
        break;
        
      case 'analytics':
        // Користувач надав згоду тільки на аналітику
        window.gtag('consent', 'update', {
          'analytics_storage': 'granted',
          'ad_storage': 'denied',
          'ad_user_data': 'denied',
          'ad_personalization': 'denied'
        });
        break;
        
      case 'ads':
        // Користувач надав згоду тільки на рекламу
        window.gtag('consent', 'update', {
          'ad_storage': 'granted',
          'ad_user_data': 'granted',
          'ad_personalization': 'granted',
          'analytics_storage': 'denied'
        });
        break;
        
      case 'none':
      default:
        // Користувач відхилив всі типи обробки даних
        window.gtag('consent', 'update', {
          'ad_storage': 'denied',
          'ad_user_data': 'denied',
          'ad_personalization': 'denied',
          'analytics_storage': 'denied'
        });
        break;
    }
  } catch (error) {
    console.error('GTM: Помилка при оновленні згоди', error);
  }
};

/**
 * Відстеження перегляду сторінки
 * @param {string} path - Шлях до сторінки
 * @param {string} title - Заголовок сторінки
 * @param {string} language - Мова сторінки
 */
export const pageView = (path, title, language) => {
  if (!window.dataLayer) {
    initDataLayer();
  }
  
  window.dataLayer.push({
    event: 'pageview',
    page: {
      path,
      title,
      language
    }
  });
};

/**
 * Відстеження події
 * @param {string} eventName - Назва події
 * @param {object} eventParams - Параметри події
 * @param {string} language - Мова інтерфейсу
 */
export const trackEvent = (eventName, eventParams = {}, language) => {
  if (!window.dataLayer) {
    initDataLayer();
  }
  
  window.dataLayer.push({
    event: eventName,
    language,
    ...eventParams
  });
};

/**
 * Відстеження форми
 * @param {string} formName - Назва форми
 * @param {string} formAction - Дія форми (submit, error, success)
 * @param {object} formData - Дані форми (без чутливої інформації)
 * @param {string} language - Мова інтерфейсу
 */
export const trackForm = (formName, formAction, formData = {}, language) => {
  if (!window.dataLayer) {
    initDataLayer();
  }
  
  window.dataLayer.push({
    event: 'form_interaction',
    form_name: formName,
    form_action: formAction,
    language,
    ...formData
  });
};

/**
 * Відстеження помилок
 * @param {string} errorMessage - Повідомлення про помилку
 * @param {string} errorCode - Код помилки (опціонально)
 * @param {string} language - Мова інтерфейсу
 */
export const trackError = (errorMessage, errorCode, language) => {
  if (!window.dataLayer) {
    initDataLayer();
  }
  
  window.dataLayer.push({
    event: 'error',
    error_message: errorMessage,
    error_code: errorCode,
    language
  });
};

/**
 * Відстеження мовної преференції користувача
 * @param {string} language - Поточна мова
 * @param {string} previousLanguage - Попередня мова
 */
export const trackLanguageChange = (language, previousLanguage) => {
  if (!window.dataLayer) {
    initDataLayer();
  }
  
  window.dataLayer.push({
    event: 'language_change',
    current_language: language,
    previous_language: previousLanguage
  });
};

// Об'єкт з усіма утилітами для GTM
const gtmUtils = {
  initDataLayer,
  pageView,
  trackEvent,
  trackForm,
  trackError,
  updateConsent,
  trackLanguageChange
};

export default gtmUtils; 